import { useQuery } from '@tanstack/react-query';
import api from 'src/app/core/api/apiProvider';
import {
  DEFAULT_FILTER_CATEGORIES,
  FILTER_CATEGORIES_ENDPOINT,
  FilterCategories,
  normalizeFilterCategories,
} from '../utils/filterCategories';

export const FILTER_CATEGORIES_QUERY_KEY = ['filterCategories'];

export const fetchFilterCategories = async (): Promise<FilterCategories> => {
  const { data } = await api.get(
    `${import.meta.env.VITE_API_URL}${FILTER_CATEGORIES_ENDPOINT}`
  );

  const categories = normalizeFilterCategories(data);

  // `[]` is a real answer (an organization with nothing configured), so only a
  // null normalization is a failure. Throwing on it lets react-query retry and
  // keeps `isFallback` honest.
  if (!categories) {
    throw new Error('Browse filters response was unreadable');
  }

  return categories;
};

export interface UseFilterCategoriesResult {
  /** Ordered as the backend returned them. Render chips in this order. */
  categories: FilterCategories;
  isFetching: boolean;
  /** True while the shipped defaults are on screen instead of backend data. */
  isFallback: boolean;
  isError: boolean;
}

/**
 * Filter categories from `GET /contents/browse_filters/`, with the shipped
 * defaults as the fallback for a failed or unreadable request.
 *
 * An empty array from the backend is passed through untouched: an organization
 * with nothing configured renders no filter chips, which is the intended
 * behavior and not a reason to fall back.
 */
const useFilterCategories = (): UseFilterCategoriesResult => {
  const { data, isFetching, isError } = useQuery({
    queryKey: FILTER_CATEGORIES_QUERY_KEY,
    queryFn: fetchFilterCategories,
    // The taxonomy is admin-authored and changes about as often as a release.
    // No need to refetch it every time the Browser mounts.
    staleTime: 1000 * 60 * 60,
    // Retry transient failures once, never a 4xx. The endpoint always answers
    // 200, so a 4xx here is auth or a moved route: retrying fixes neither.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });

  return {
    categories: data ?? DEFAULT_FILTER_CATEGORIES,
    isFetching,
    isFallback: !data,
    isError,
  };
};

export default useFilterCategories;
